import {
  Areas,
  Design,
  LogicalSpace,
  ManualArea,
  Obstacle,
  RackingArea,
  Segment,
  SingularStationArea,
  StationaryArea,
  Templates,
} from '@thive/fddm-types';
import { Vector2 } from 'three';

import {
  findPolygonOverlap,
  makeOffsetPoly,
  polygonCentroid,
} from '@/modules/common/helpers/polygon';
import { uniqueBy } from '@/modules/common/helpers/array';
import { assertNever } from '@/helpers/utils';
import {
  areasToFlatCollection,
  createEmptyAreas,
  templatesToFlatCollection,
  SpatialArea2DToPolygon,
} from '@/modules/floorplanDesign/helpers/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { Area, SpatialArea2D } from '@/modules/floorplanDesign/types';
import { isHighwayArea } from '@/modules/floorplanDesign/helpers';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import {
  isExtractionStationaryArea,
  isInsertionStationaryArea,
  isManualArea,
} from '@/modules/floorplanDesign/helpers/typeguards';

import { useCollisionStore } from '../store/useCollisionStore';
import { ICollisionService } from './ICollisionService';
import { Collision } from '../types';

type SpatialType =
  | 'area'
  | 'obstacle'
  | 'wall'
  | 'logicalSpace'
  | 'highwayArea'
  | 'manualArea'
  | 'extractionStationaryArea'
  | 'insertionStationaryArea';

interface AABB {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

interface CollisionSource {
  key: string;
  spatialIds: string[];
  spatials: SpatialArea2D[];
  bounds: AABB;
}

export class CollisionServiceImpl implements ICollisionService {
  private collisionStore: typeof useCollisionStore;
  private floorplanDesignStore: typeof useFloorplanDesignStore;
  private polygonCache: Map<string, Vector2[]> = new Map();
  private boundsCache: Map<string, AABB> = new Map();
  private offsetPolyCache: Map<string, Vector2[]> = new Map();
  private spatialTypeMap: Map<string, SpatialType> = new Map();
  private spatialIdMap: Map<string, string> = new Map();
  private areaTemplateMap: Map<string, string> = new Map();
  private templateSpatialMap: Map<string, SpatialArea2D[]> = new Map();
  private templateBoundsMap: Map<string, AABB> = new Map();

  public constructor(
    collisionStore: typeof useCollisionStore,
    floorplanDesignStore: typeof useFloorplanDesignStore,
  ) {
    this.collisionStore = collisionStore;
    this.floorplanDesignStore = floorplanDesignStore;
  }

  public initialize(source: {
    areas?: Areas;
    obstacles?: Obstacle[];
    logicalSpaces?: LogicalSpace[];
  }): { newCollisions: Collision[]; removedCollisions: Collision[] } {
    this.clearCaches();
    const { apply, collisionState } = this.collisionStore.getState();

    const { collisionsToAdd, collisionsToRemove } = this.determineCollisionUpdates(
      source.areas ?? createEmptyAreas(),
      source.obstacles ?? [],
      source.logicalSpaces ?? [],
      createEmptyAreas(),
      [],
      [],
      this.floorplanDesignStore.getState().design,
      collisionState,
      true,
    );

    apply(collisionsToAdd, collisionsToRemove);

    return { newCollisions: collisionsToAdd, removedCollisions: collisionsToRemove };
  }

  public update(source: {
    updatedAreas?: Areas;
    updatedObstacles?: Obstacle[];
    updatedLogicalSpaces?: LogicalSpace[];
    deletedAreas?: Areas;
    deletedObstacles?: Obstacle[];
    deletedLogicalSpaces?: LogicalSpace[];
  }): { newCollisions: Collision[]; removedCollisions: Collision[] } {
    this.clearCaches();
    const { apply, collisionState } = this.collisionStore.getState();

    const { collisionsToAdd, collisionsToRemove } = this.determineCollisionUpdates(
      source.updatedAreas ?? createEmptyAreas(),
      source.updatedObstacles ?? [],
      source.updatedLogicalSpaces ?? [],
      source.deletedAreas ?? createEmptyAreas(),
      source.deletedObstacles ?? [],
      source.deletedLogicalSpaces ?? [],
      this.floorplanDesignStore.getState().design,
      collisionState,
      true,
    );

    // One store write for all changes (both directions are stored)
    apply(collisionsToAdd, collisionsToRemove);

    return { newCollisions: collisionsToAdd, removedCollisions: collisionsToRemove };
  }

  //
  // Private methods
  //

  private collision_tolerance_in_meter = 0.03;

  private clearCaches(): void {
    this.polygonCache.clear();
    this.boundsCache.clear();
    this.offsetPolyCache.clear();
    this.spatialTypeMap.clear();
    this.spatialIdMap.clear();
    this.areaTemplateMap.clear();
    this.templateSpatialMap.clear();
    this.templateBoundsMap.clear();
  }

  private getBroadPhaseBounds(spatial: SpatialArea2D): AABB {
    const templateId = this.areaTemplateMap.get(spatial.id);
    if (templateId) {
      const templateBounds = this.templateBoundsMap.get(templateId);
      if (templateBounds) return templateBounds;
    }

    return this.getBounds(spatial);
  }

  private createCollisionSourceFromSpatial(
    key: string,
    spatials: SpatialArea2D[],
    spatialIds: string[],
  ): CollisionSource {
    return {
      key,
      spatialIds,
      spatials,
      bounds: this.getMergedBounds(spatials),
    };
  }

  private getMergedBounds(spatials: SpatialArea2D[]): AABB {
    if (spatials.length === 0) {
      return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    }

    const firstBounds = this.getBounds(spatials[0]);
    const mergedBounds: AABB = { ...firstBounds };

    for (let i = 1; i < spatials.length; i++) {
      const bounds = this.getBounds(spatials[i]);
      if (bounds.minX < mergedBounds.minX) mergedBounds.minX = bounds.minX;
      if (bounds.maxX > mergedBounds.maxX) mergedBounds.maxX = bounds.maxX;
      if (bounds.minY < mergedBounds.minY) mergedBounds.minY = bounds.minY;
      if (bounds.maxY > mergedBounds.maxY) mergedBounds.maxY = bounds.maxY;
    }

    return mergedBounds;
  }

  private buildCollisionSources(
    areaSpatials: SpatialArea2D[],
    obstacleSpatials: SpatialArea2D[],
    logicalSpaceSpatials: SpatialArea2D[],
    templates: Templates,
  ): CollisionSource[] {
    const sources: CollisionSource[] = [];
    const areaSpatialMap = new Map<string, SpatialArea2D[]>();

    areaSpatials.forEach((spatial) => {
      const collection = areaSpatialMap.get(spatial.id) || [];
      collection.push(spatial);
      areaSpatialMap.set(spatial.id, collection);
    });

    templatesToFlatCollection(templates).forEach((template) => {
      const templateSpatials = template.group.areaIds.flatMap(
        (areaId) => areaSpatialMap.get(areaId) || [],
      );
      this.templateSpatialMap.set(template.group.id, templateSpatials);
      this.templateBoundsMap.set(template.group.id, this.getMergedBounds(templateSpatials));
      sources.push(
        this.createCollisionSourceFromSpatial(
          template.group.id,
          templateSpatials,
          template.group.areaIds,
        ),
      );
    });

    areaSpatials.forEach((spatial) => {
      if (this.areaTemplateMap.has(spatial.id)) return;
      sources.push(this.createCollisionSourceFromSpatial(spatial.id, [spatial], [spatial.id]));
    });

    obstacleSpatials.forEach((spatial) => {
      sources.push(this.createCollisionSourceFromSpatial(spatial.id, [spatial], [spatial.id]));
    });

    logicalSpaceSpatials.forEach((spatial) => {
      sources.push(this.createCollisionSourceFromSpatial(spatial.id, [spatial], [spatial.id]));
    });

    return sources;
  }

  private isCollisionRelevant(typeA: SpatialType, typeB: SpatialType): boolean {
    // Rules: only these combinations are relevant
    const validPairs = [
      ['highwayArea', 'highwayArea'],
      ['area', 'highwayArea'],
      ['highwayArea', 'area'],
      ['obstacle', 'highwayArea'],
      ['highwayArea', 'obstacle'],
      ['logicalSpace', 'highwayArea'],
      ['highwayArea', 'logicalSpace'],
      ['area', 'obstacle'],
      ['obstacle', 'area'],
      ['area', 'logicalSpace'],
      ['logicalSpace', 'area'],
      ['wall', 'highwayArea'],
      ['highwayArea', 'wall'],
      ['area', 'wall'],
      ['wall', 'area'],
      ['manualArea', 'extractionStationaryArea'],
      ['extractionStationaryArea', 'manualArea'],
      ['manualArea', 'insertionStationaryArea'],
      ['insertionStationaryArea', 'manualArea'],
      ['extractionStationaryArea', 'highwayArea'],
      ['highwayArea', 'extractionStationaryArea'],
      ['insertionStationaryArea', 'highwayArea'],
      ['highwayArea', 'insertionStationaryArea'],
    ];

    return validPairs.some(
      ([a, b]) => (a === typeA && b === typeB) || (a === typeB && b === typeA),
    );
  }

  private getCachedPolygon(spatial: SpatialArea2D): Vector2[] {
    const cached = this.polygonCache.get(spatial.id);
    if (cached) return cached;
    const polygon = SpatialArea2DToPolygon(spatial);
    this.polygonCache.set(spatial.id, polygon);
    return polygon;
  }

  private getBounds(spatial: SpatialArea2D): AABB {
    const cached = this.boundsCache.get(spatial.id);
    if (cached) return cached;
    const polygon = this.getCachedPolygon(spatial);
    const bounds: AABB = {
      minX: polygon[0].x - this.collision_tolerance_in_meter,
      minY: polygon[0].y - this.collision_tolerance_in_meter,
      maxX: polygon[0].x + this.collision_tolerance_in_meter,
      maxY: polygon[0].y + this.collision_tolerance_in_meter,
    };
    for (let i = 1; i < polygon.length; i++) {
      const point = polygon[i];
      if (point.x < bounds.minX) bounds.minX = point.x - this.collision_tolerance_in_meter;
      if (point.x > bounds.maxX) bounds.maxX = point.x + this.collision_tolerance_in_meter;
      if (point.y < bounds.minY) bounds.minY = point.y - this.collision_tolerance_in_meter;
      if (point.y > bounds.maxY) bounds.maxY = point.y + this.collision_tolerance_in_meter;
    }
    this.boundsCache.set(spatial.id, bounds);
    return bounds;
  }

  private aabbOverlap(a: AABB, b: AABB): boolean {
    return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;
  }

  private getCachedOffsetPoly(points: Vector2[]): Vector2[] {
    const cacheKey = `${this.collision_tolerance_in_meter}_${points
      .map((p) => `${p.x},${p.y}`)
      .join('|')}`;
    const cached = this.offsetPolyCache.get(cacheKey);
    if (cached) return cached;
    const offsetPoly = makeOffsetPoly(points, this.collision_tolerance_in_meter);
    this.offsetPolyCache.set(cacheKey, offsetPoly);
    return offsetPoly;
  }

  private segmentToSpatialArea(segment: Segment, id: string): SpatialArea2D {
    return {
      id,
      type: 'segment',
      data: {
        start: segment.start,
        end: segment.end,
        leftSideWidth: segment.leftSideWidth,
        rightSideWidth: segment.rightSideWidth,
      },
    };
  }

  private getSpatialAreaType(
    item: ManualArea | RackingArea | SingularStationArea | StationaryArea,
  ): Exclude<SpatialType, 'obstacle' | 'wall' | 'logicalSpace' | 'highwayArea'> {
    if (isManualArea(item)) {
      return 'manualArea';
    } else if (isExtractionStationaryArea(item)) {
      return 'extractionStationaryArea';
    } else if (isInsertionStationaryArea(item)) {
      return 'insertionStationaryArea';
    }

    return 'area';
  }

  private itemToSpatial(
    params:
      | {
          item: Area;
          type: 'Area';
        }
      | {
          item: LogicalSpace;
          type: 'LogicalSpace';
        }
      | {
          item: Obstacle;
          type: 'Obstacle';
        }
      | {
          item: Obstacle;
          type: 'Wall';
        },
  ): SpatialArea2D[] {
    const { item, type } = params;

    switch (type) {
      case 'LogicalSpace': {
        const polygon = RectangleUtils.polygonToRectangle(item.polygon);
        if (!polygon) return [];
        const spatial: SpatialArea2D = { id: item.id, type: 'rectangle', data: polygon };
        this.spatialTypeMap.set(item.id, 'logicalSpace');
        this.spatialIdMap.set(item.id, item.id);
        return [spatial];
      }
      case 'Obstacle': {
        return item.segments.map((segment) => {
          const spatial = this.segmentToSpatialArea(segment, item.id);
          this.spatialTypeMap.set(item.id, 'obstacle');
          this.spatialIdMap.set(item.id, item.id);
          return spatial;
        });
      }
      case 'Wall': {
        return item.segments.map((segment) => {
          const spatial = this.segmentToSpatialArea(segment, segment.id);
          this.spatialTypeMap.set(segment.id, 'wall');
          this.spatialIdMap.set(segment.id, item.id);
          return spatial;
        });
      }
      case 'Area': {
        if (isHighwayArea(item)) {
          return item.segments.map((segment) => {
            const spatial = this.segmentToSpatialArea(segment, segment.id);
            this.spatialTypeMap.set(segment.id, 'highwayArea');
            this.spatialIdMap.set(segment.id, segment.id);
            return spatial;
          });
        }
        const type = this.getSpatialAreaType(item);
        const spatial: SpatialArea2D = { id: item.id, type: 'rectangle', data: item.rectangle };
        this.spatialTypeMap.set(item.id, type);
        this.spatialIdMap.set(item.id, item.id);
        return [spatial];
      }
      default:
        assertNever(type);
    }
  }

  private findOverlapCenter(polyA: Vector2[], polyB: Vector2[]) {
    const polyWithMarginA = this.getCachedOffsetPoly(polyA.map((p) => new Vector2(p.x, p.y)));
    const polyWithMarginB = this.getCachedOffsetPoly(polyB.map((p) => new Vector2(p.x, p.y)));
    const intersectionPolygon = findPolygonOverlap(polyWithMarginA, polyWithMarginB);
    if (!intersectionPolygon) return null;

    return polygonCentroid(intersectionPolygon);
  }

  private findPotentialTargets(source: SpatialArea2D, candidates: SpatialArea2D[]) {
    const sourceBounds = this.getBroadPhaseBounds(source);
    const sourceType = this.spatialTypeMap.get(source.id);
    const sourceTemplateId = this.areaTemplateMap.get(source.id);

    if (!sourceType) return [];

    return candidates
      .filter((item) => {
        if (source.id === item.id) return false;
        const itemTemplateId = this.areaTemplateMap.get(item.id);
        if (sourceTemplateId && itemTemplateId && sourceTemplateId === itemTemplateId) return false;

        // Type-based early rejection - skip irrelevant type combinations
        const itemType = this.spatialTypeMap.get(item.id);
        if (!itemType) return false;
        if (!this.isCollisionRelevant(sourceType, itemType)) return false;

        const itemBounds = this.getBroadPhaseBounds(item);
        return this.aabbOverlap(sourceBounds, itemBounds);
      })
      .flatMap((item) => {
        const point = this.findOverlapCenter(
          this.getCachedPolygon(source),
          this.getCachedPolygon(item),
        );
        if (!point) return [];
        return [{ target: item, point }];
      });
  }

  private findCollisions(source: CollisionSource, candidates: SpatialArea2D[]): Collision[] {
    return source.spatials.flatMap((sourceSpatial) => {
      const potentialTargets = this.findPotentialTargets(sourceSpatial, candidates);
      return potentialTargets.flatMap(({ target }) => {
        const sourceId = this.spatialIdMap.get(sourceSpatial.id);
        const targetId = this.spatialIdMap.get(target.id);

        if (!sourceId || !targetId) return [];
        return {
          idA: sourceId,
          idB: targetId,
        };
      });
    });
  }

  private determineCollisionUpdates(
    areaUpdates: Areas,
    obstacleUpdates: Obstacle[],
    logicalSpaceUpdates: LogicalSpace[],
    deletedAreas: Areas,
    deletedObstacles: Obstacle[],
    deletedLogicalSpaces: LogicalSpace[],
    fpDesign: Design,
    collisionState: Map<string, string[]>,
    checkAgainstUpdatedItems: boolean = true,
  ) {
    const collisionsToAdd: Collision[] = [];
    const collisionsToRemove: Collision[] = [];
    const { areas, obstacles, logicalSpaces, templates } = fpDesign;

    templatesToFlatCollection(templates).forEach((template) => {
      template.group.areaIds.forEach((areaId) => {
        this.areaTemplateMap.set(areaId, template.group.id);
      });
    });

    const updatedAreaIds = new Set(areasToFlatCollection(areaUpdates).map((a) => a.id));

    // Updated items go first so uniqueBy keeps their NEW geometry: the store still holds the old
    // geometry at this point, and an item may only exist once (the polygon/bounds caches are keyed by id).
    const allAreasFlat = uniqueBy(
      [...areasToFlatCollection(areaUpdates), ...areasToFlatCollection(areas)].filter(
        (area) =>
          !this.areaTemplateMap.has(area.id) &&
          (checkAgainstUpdatedItems || !updatedAreaIds.has(area.id)),
      ),
      (area) => area.id,
    );
    const allObstaclesFlat = uniqueBy(
      [...obstacleUpdates, ...obstacles].filter(
        (o) => checkAgainstUpdatedItems || !obstacleUpdates.some((uo) => uo.id === o.id),
      ),
      (o) => o.id,
    );

    const allLogicalSpacesFlat = uniqueBy(
      [...logicalSpaceUpdates, ...logicalSpaces].filter(
        (ls) => checkAgainstUpdatedItems || !logicalSpaceUpdates.some((uls) => uls.id === ls.id),
      ),
      (ls) => ls.id,
    );

    // Filter updated items similarly
    const updatedAreaSpatials: SpatialArea2D[] = areasToFlatCollection(areaUpdates)
      .filter((area) => !this.areaTemplateMap.has(area.id))
      .flatMap((area) => this.itemToSpatial({ item: area, type: 'Area' }));
    const updatedLogicalSpaceSpatials: SpatialArea2D[] = logicalSpaceUpdates.flatMap(
      (logicalSpace) => this.itemToSpatial({ item: logicalSpace, type: 'LogicalSpace' }),
    );
    const updatedObstacleSpatials: SpatialArea2D[] = obstacleUpdates.flatMap((o) =>
      o.appearance === 'WALL'
        ? this.itemToSpatial({ item: o, type: 'Wall' })
        : this.itemToSpatial({ item: o, type: 'Obstacle' }),
    );
    const allAreaSpatials = allAreasFlat.flatMap((item) =>
      this.itemToSpatial({ item, type: 'Area' }),
    );
    const allLogicalSpaceSpatials = allLogicalSpacesFlat.flatMap((item) =>
      this.itemToSpatial({ item, type: 'LogicalSpace' }),
    );
    const allObstacleSpatials = allObstaclesFlat.flatMap((item) =>
      item.appearance === 'WALL'
        ? this.itemToSpatial({ item, type: 'Wall' })
        : this.itemToSpatial({ item, type: 'Obstacle' }),
    );

    const allItemSpatials = [
      ...allAreaSpatials,
      ...allObstacleSpatials,
      ...allLogicalSpaceSpatials,
    ];

    const collisionSources = this.buildCollisionSources(
      updatedAreaSpatials,
      updatedObstacleSpatials,
      updatedLogicalSpaceSpatials,
      templates,
    );

    collisionSources.forEach((source) => {
      const newCollisions: string[] = [];
      const redundantCollisions: string[] = [];
      // Process each detected collision
      const sourceIds = source.spatialIds.flatMap((spatialId) => {
        const id = this.spatialIdMap.get(spatialId);
        if (!id) return [];
        return [id];
      });

      const existingCollisions = sourceIds.flatMap((id) => collisionState.get(id) || []);
      const uniqueExistingCollisions = [...new Set(existingCollisions)];
      const detectedCollisions = this.findCollisions(source, allItemSpatials);

      // Collisions that are no longer detected → remove
      uniqueExistingCollisions.forEach((existingCollision) => {
        const stillDetected = detectedCollisions.some((detectedCollision) => {
          return (
            detectedCollision.idA === existingCollision ||
            detectedCollision.idB === existingCollision
          );
        });
        if (!stillDetected) redundantCollisions.push(existingCollision);
      });

      detectedCollisions.forEach((collision) => {
        const existing = newCollisions.find(
          (pendingCollision) =>
            sourceIds.includes(collision.idA) &&
            pendingCollision === collision.idB &&
            existingCollisions.includes(collision.idB),
        );

        if (!existing) newCollisions.push(collision.idB);
      });

      collisionsToAdd.push(
        ...newCollisions.flatMap((idB) =>
          source.spatialIds.flatMap((idA) => {
            const id = this.spatialIdMap.get(idA);
            if (!id) return [];
            return { idA: id, idB };
          }),
        ),
      );
      collisionsToRemove.push(
        ...redundantCollisions.flatMap((idB) =>
          source.spatialIds.flatMap((idA) => {
            const id = this.spatialIdMap.get(idA);
            if (!id) return [];
            return { idA: id, idB };
          }),
        ),
      );
    });

    const deletedItem = [
      ...areasToFlatCollection(deletedAreas).flatMap((area) =>
        isHighwayArea(area) ? area.segments.map((s) => s.id) : [area.id],
      ),
      ...deletedObstacles.map((o) => o.id),
      ...deletedLogicalSpaces.map((ls) => ls.id),
    ];

    new Set(deletedItem).forEach((deletedItemId) => {
      const collisionsFrom = collisionState.get(deletedItemId) || [];
      const collisionToKeys = [...collisionState.entries()]
        .filter(([, collisionIds]) => collisionIds.includes(deletedItemId))
        .map(([collisionId]) => collisionId);

      collisionsFrom.forEach((idB) => {
        if (collisionState.get(deletedItemId)?.includes(idB)) {
          collisionsToRemove.push({ idA: deletedItemId, idB });
        }
        if (collisionState.get(idB)?.includes(deletedItemId)) {
          collisionsToRemove.push({ idA: idB, idB: deletedItemId });
        }
      });

      collisionToKeys.forEach((idB) => {
        if (collisionState.get(deletedItemId)?.includes(idB)) {
          collisionsToRemove.push({ idA: deletedItemId, idB });
        }
        if (collisionState.get(idB)?.includes(deletedItemId)) {
          collisionsToRemove.push({ idA: idB, idB: deletedItemId });
        }
      });
    });

    return { collisionsToAdd, collisionsToRemove };
  }
}
